import React, { useMemo, useRef } from 'react';
import { colorWithAlpha, mixColors, parseColorToRgb } from './liquidShared';

export const PUMP_ASSEMBLY_SCENE = {
  width: 420,
  height: 220,
  leftPipe: { x: 60, y: 135, width: 138, height: 52 },
  elbow: { x: 174, y: 60, width: 101, height: 74 },
  topPipe: { x: 250, y: 49, width: 158, height: 59 },
  motor: { x: 192, y: 119, width: 120, height: 78 },
};

export const PIPE_VIEWBOX = {
  width: 320,
  height: 120,
};

export const ELBOW_VIEWBOX = {
  width: 180,
  height: 132,
};

const LEGACY_PIPE_WATER_COLORS = ['#00BFFF', '#00bfff'];
const LEGACY_PIPE_COLORS = ['rgba(255, 255, 255, 0.08)', 'rgba(255,255,255,0.08)'];
const LEGACY_PIPE_BORDER_COLORS = ['rgba(255, 255, 255, 0.16)', 'rgba(255,255,255,0.16)'];
const LEGACY_CLAMP_COLORS = ['rgba(125, 170, 170, 0.65)', 'rgba(125,170,170,0.65)'];
const LEGACY_ELBOW_WATER_COLORS = ['#f0143c', '#F0143C', '#87D4FF'];
const LEGACY_ELBOW_SHELL_COLORS = ['rgba(240, 178, 188, 0.88)', 'rgba(168, 215, 228, 0.84)'];
const LEGACY_ELBOW_PIPE_GLASS_COLORS = ['rgba(142, 190, 214, 0.44)'];
const LEGACY_ELBOW_PIPE_BORDER_COLORS = ['rgba(156, 194, 216, 0.42)'];
const LEGACY_ELBOW_CLAMP_COLORS = ['rgba(118, 152, 153, 0.92)'];

const ELBOW_PIPE_PATH = 'M69 25 L69 79 Q69 101 93 101 L151 101';
const ELBOW_VERTICAL_DROP_PATH = 'M80 35 L80 81 Q80 92 90 96';
const ELBOW_ORIENTATION_TRANSFORMS = {
  'top-right': '',
  'top-left': `translate(${ELBOW_VIEWBOX.width} 0) scale(-1 1)`,
  'bottom-right': `translate(0 ${ELBOW_VIEWBOX.height}) scale(1 -1)`,
  'bottom-left': `translate(${ELBOW_VIEWBOX.width} ${ELBOW_VIEWBOX.height}) scale(-1 -1)`,
};

const clampValue = (value, min, max) => Math.min(max, Math.max(min, value));

const colorsEqual = (left, right) => {
  const rgbLeft = parseColorToRgb(left);
  const rgbRight = parseColorToRgb(right);
  if (!rgbLeft || !rgbRight) {
    return String(left || '').trim() === String(right || '').trim();
  }

  return rgbLeft.every((channel, index) => channel === rgbRight[index]);
};

const matchesAnyColor = (value, candidates) =>
  candidates.some((candidate) => colorsEqual(value, candidate));

const normalizeColor = (value, fallback, legacyValues = []) => {
  const normalized = String(value || '').trim();
  if (!normalized || matchesAnyColor(normalized, legacyValues)) {
    return fallback;
  }
  return normalized;
};

const createWaveFillPath = ({
  xStart,
  xEnd,
  baseY,
  amplitude,
  wavelength,
  floorY,
}) => {
  let x = xStart;
  let d = `M${xStart} ${baseY}`;

  while (x < xEnd) {
    const nextX = Math.min(x + wavelength, xEnd);
    const span = nextX - x;
    const quarter = span * 0.25;
    const half = span * 0.5;

    d += ` C ${x + quarter} ${baseY - amplitude}, ${x + half - quarter} ${baseY - amplitude}, ${x + half} ${baseY}`;
    d += ` C ${x + half + quarter} ${baseY + amplitude}, ${nextX - quarter} ${baseY + amplitude}, ${nextX} ${baseY}`;
    x = nextX;
  }

  d += ` L ${xEnd} ${floorY} L ${xStart} ${floorY} Z`;
  return d;
};

const createWaveBandPath = ({
  xStart,
  xEnd,
  topY,
  baseY,
  amplitude,
  wavelength,
}) => {
  let x = xEnd;
  let d = `M${xStart} ${topY} L ${xEnd} ${topY} L ${xEnd} ${baseY}`;

  while (x > xStart) {
    const nextX = Math.max(x - wavelength, xStart);
    const span = x - nextX;
    const quarter = span * 0.25;
    const half = span * 0.5;

    d += ` C ${x - quarter} ${baseY + amplitude}, ${x - half + quarter} ${baseY + amplitude}, ${x - half} ${baseY}`;
    d += ` C ${x - half - quarter} ${baseY - amplitude}, ${nextX + quarter} ${baseY - amplitude}, ${nextX} ${baseY}`;
    x = nextX;
  }

  d += ' Z';
  return d;
};

const getIsFlowing = (value) => {
  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return numericValue !== 0;
  }

  return Boolean(value);
};

const resolveFlowDirection = (direction, flowDirection) => {
  const normalizedDirection = String(direction || 'horizontal').trim().toLowerCase();
  const normalizedFlowDirection = String(flowDirection || '').trim().toLowerCase();

  if (normalizedDirection === 'vertical') {
    return normalizedFlowDirection === 'bottom-to-top' ? 'bottom-to-top' : 'top-to-bottom';
  }

  return normalizedFlowDirection === 'right-to-left' ? 'right-to-left' : 'left-to-right';
};

export const PumpAssemblyPipeGraphic = ({
  idBase,
  title,
  value,
  color,
  pipeColor,
  pipeBackgroundColor,
  pipeGlassColor,
  pipeBorderColor,
  clampColor,
  direction = 'horizontal',
  flowDirection = 'left-to-right',
  pipeHeight = 70,
  pipeBorderWidth = 10,
  pipeRadius = 18,
  clampCount = 4,
  clampWidth = 22,
  clampRadius = 16,
  waterOpacity = 0.85,
  waveSpeed = 2.75,
}) => {
  const instanceIdRef = useRef(`pump-pipe-${Math.random().toString(36).slice(2, 10)}`);
  const isFlowing = getIsFlowing(value);
  const resolvedFlowDirection = useMemo(
    () => resolveFlowDirection(direction, flowDirection),
    [direction, flowDirection]
  );
  const usesReverseAnimationDirection = resolvedFlowDirection === 'left-to-right';

  const defaultWaterColor = useMemo(
    () => mixColors('var(--icon-reset-password)', 'var(--part-color)', 0.28),
    []
  );
  const defaultPipeColor = useMemo(
    () => mixColors('var(--header-color)', 'var(--part-color)', 0.24),
    []
  );
  const defaultPipeGlassColor = useMemo(
    () => mixColors('var(--part-color)', 'var(--cell-background)', 0.24),
    []
  );
  const defaultPipeBorderColor = useMemo(
    () => mixColors(defaultPipeColor, 'var(--part-color)', 0.28),
    [defaultPipeColor]
  );
  const defaultClampColor = useMemo(
    () => mixColors('var(--header-color)', 'var(--part-color)', 0.18),
    []
  );

  const resolvedWaterColor = useMemo(
    () => normalizeColor(color, defaultWaterColor, LEGACY_PIPE_WATER_COLORS),
    [color, defaultWaterColor]
  );
  const resolvedPipeColor = useMemo(
    () => normalizeColor(pipeColor, defaultPipeColor, LEGACY_PIPE_COLORS),
    [defaultPipeColor, pipeColor]
  );
  const defaultPipeBackgroundColor = useMemo(
    () => mixColors('var(--header-background)', resolvedPipeColor, 0.14),
    [resolvedPipeColor]
  );
  const resolvedPipeBackgroundColor = useMemo(
    () => normalizeColor(pipeBackgroundColor, defaultPipeBackgroundColor),
    [defaultPipeBackgroundColor, pipeBackgroundColor]
  );
  const resolvedPipeGlassColor = useMemo(
    () => normalizeColor(pipeGlassColor, defaultPipeGlassColor, [...LEGACY_PIPE_COLORS, defaultPipeColor]),
    [defaultPipeColor, defaultPipeGlassColor, pipeGlassColor]
  );
  const resolvedPipeBorderColor = useMemo(
    () => normalizeColor(pipeBorderColor, defaultPipeBorderColor, LEGACY_PIPE_BORDER_COLORS),
    [defaultPipeBorderColor, pipeBorderColor]
  );
  const resolvedClampColor = useMemo(
    () => normalizeColor(clampColor, defaultClampColor, LEGACY_CLAMP_COLORS),
    [clampColor, defaultClampColor]
  );

  const interiorBaseColor = resolvedPipeBackgroundColor;
  const shellLight = useMemo(
    () => mixColors(resolvedPipeColor, 'var(--part-color)', 0.34),
    [resolvedPipeColor]
  );
  const shellDark = useMemo(
    () => mixColors(resolvedPipeColor, 'var(--shadow-black-soft)', 0.28),
    [resolvedPipeColor]
  );
  const glassGlow = useMemo(
    () => mixColors(resolvedPipeGlassColor, 'var(--part-color)', 0.42),
    [resolvedPipeGlassColor]
  );
  const cavityLight = useMemo(
    () => mixColors('var(--header-background)', interiorBaseColor, isFlowing ? 0.22 : 0.08),
    [interiorBaseColor, isFlowing]
  );
  const cavityDark = useMemo(
    () => mixColors('var(--shadow-black-soft)', interiorBaseColor, isFlowing ? 0.08 : 0.18),
    [interiorBaseColor, isFlowing]
  );
  const waterLight = useMemo(
    () => mixColors(resolvedWaterColor, 'var(--part-color)', 0.54),
    [resolvedWaterColor]
  );
  const waterMid = useMemo(
    () => mixColors(resolvedWaterColor, 'var(--header-background)', 0.18),
    [resolvedWaterColor]
  );
  const waterDark = useMemo(
    () => mixColors(resolvedWaterColor, 'var(--shadow-black-soft)', 0.34),
    [resolvedWaterColor]
  );
  const clampLight = useMemo(
    () => mixColors(resolvedClampColor, 'var(--part-color)', 0.18),
    [resolvedClampColor]
  );
  const clampDark = useMemo(
    () => mixColors(resolvedClampColor, 'var(--shadow-black-soft)', 0.24),
    [resolvedClampColor]
  );
  const pipeSurfaceHighlight = useMemo(
    () => colorWithAlpha(mixColors(resolvedPipeBorderColor, 'var(--part-color)', 0.48), 0.46),
    [resolvedPipeBorderColor]
  );
  const clampInnerHighlight = useMemo(
    () => colorWithAlpha(mixColors(resolvedClampColor, 'var(--part-color)', 0.42), 0.42),
    [resolvedClampColor]
  );

  const safeWaterOpacity = clampValue(Number(waterOpacity) || 0.85, 0.28, 1);
  const safeWaveSpeed = clampValue(Number(waveSpeed) || 2.75, 0.8, 12);
  const safeClampCount = clampValue(Math.round(Number(clampCount) || 4), 2, 8);
  const safePipeHeight = clampValue(Number(pipeHeight) || 70, 20, 108);
  const waveAmplitude = isFlowing ? 4.4 : 3.2;
  const waveDuration = isFlowing ? safeWaveSpeed : safeWaveSpeed * 1.55;
  const secondaryWaveDuration = waveDuration * 1.32;
  const sheenDuration = waveDuration * 1.18;
  const glassDuration = waveDuration * 1.9;

  const outerHeight = clampValue(safePipeHeight * 0.66, 16, 62);
  const outerY = (PIPE_VIEWBOX.height - outerHeight) / 2;
  const outerWidth = PIPE_VIEWBOX.width;
  const outerRadius = clampValue((Number(pipeRadius) || 18) * 1.05, outerHeight / 3, outerHeight / 2);
  const pipeThicknessScale = outerHeight / 46.2;
  const effectivePipeBorderWidth = (Number(pipeBorderWidth) || 10) * pipeThicknessScale;

  const innerInset = clampValue(effectivePipeBorderWidth * 0.5, 3, 11);
  const innerX = innerInset;
  const innerY = outerY + innerInset + 0.5;
  const innerWidth = outerWidth - innerInset * 2;
  const innerHeight = outerHeight - innerInset * 2 - 1;
  const innerRadius = clampValue(outerRadius - innerInset * 0.72, innerHeight / 3, innerHeight / 2);

  const clampBodyWidth = clampValue((Number(clampWidth) || 22) * 0.56 * pipeThicknessScale, 5, 30);
  const clampBodyHeight = outerHeight + (18 * Math.max(0.58, pipeThicknessScale));
  const clampBodyY = (PIPE_VIEWBOX.height - clampBodyHeight) / 2;
  const clampBodyRadius = clampValue((Number(clampRadius) || 16) * 0.48 * pipeThicknessScale, clampBodyWidth / 2, 16);
  const clampInnerWidth = clampBodyWidth * 0.52;
  const clampInnerHeight = clampBodyHeight * 0.72;

  const clampCenters = useMemo(() => {
    if (safeClampCount <= 1) {
      return [outerWidth / 2];
    }

    const edgeInset = Math.max(clampBodyWidth / 2 + 4, 14);
    return Array.from({ length: safeClampCount }, (_, index) => {
      const ratio = index / (safeClampCount - 1);
      return edgeInset + ratio * (outerWidth - edgeInset * 2);
    });
  }, [clampBodyWidth, outerWidth, safeClampCount]);

  const waveXStart = innerX - 54;
  const waveXEnd = innerX + innerWidth + 72;
  const waterBaseY = innerY + innerHeight * (isFlowing ? 0.5 : 0.52);
  const waterFloorY = innerY + innerHeight + 10;

  const primaryWavePath = useMemo(
    () =>
      createWaveFillPath({
        xStart: waveXStart,
        xEnd: waveXEnd,
        baseY: waterBaseY,
        amplitude: waveAmplitude,
        wavelength: 44,
        floorY: waterFloorY,
      }),
    [waterBaseY, waterFloorY, waveAmplitude, waveXEnd, waveXStart]
  );
  const secondaryWavePath = useMemo(
    () =>
      createWaveFillPath({
        xStart: waveXStart - 10,
        xEnd: waveXEnd + 16,
        baseY: waterBaseY - 1.2,
        amplitude: waveAmplitude * 0.72,
        wavelength: 36,
        floorY: waterFloorY,
      }),
    [waterBaseY, waterFloorY, waveAmplitude, waveXEnd, waveXStart]
  );
  const highlightBandPath = useMemo(
    () =>
      createWaveBandPath({
        xStart: innerX - 18,
        xEnd: innerX + innerWidth + 26,
        topY: innerY + 2,
        baseY: innerY + innerHeight * 0.34,
        amplitude: 2.4,
        wavelength: 42,
      }),
    [innerHeight, innerWidth, innerX, innerY]
  );
  const highlightShadowPath = useMemo(
    () =>
      createWaveBandPath({
        xStart: innerX - 18,
        xEnd: innerX + innerWidth + 30,
        topY: innerY + innerHeight * 0.3,
        baseY: innerY + innerHeight * 0.43,
        amplitude: 1.8,
        wavelength: 44,
      }),
    [innerHeight, innerWidth, innerX, innerY]
  );

  const svgIdBase = `${String(idBase || title || 'pump-pipe').replace(/[^A-Za-z0-9_-]/g, '_')}-${instanceIdRef.current}`;
  const shellGradientId = `${svgIdBase}-shell`;
  const cavityGradientId = `${svgIdBase}-cavity`;
  const waterGradientId = `${svgIdBase}-water`;
  const clampGradientId = `${svgIdBase}-clamp`;
  const glossGradientId = `${svgIdBase}-gloss`;
  const clipPathId = `${svgIdBase}-clip`;

  return (
    <g
      style={{
        '--water-pipe-wave-duration': `${waveDuration}s`,
        '--water-pipe-wave-secondary-duration': `${secondaryWaveDuration}s`,
        '--water-pipe-sheen-duration': `${sheenDuration}s`,
        '--water-pipe-glass-duration': `${glassDuration}s`,
        '--water-pipe-flow-animation-direction': usesReverseAnimationDirection ? 'reverse' : 'normal',
      }}
    >
      <defs>
        <linearGradient id={shellGradientId} x1="34" y1="32" x2="286" y2="88" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={colorWithAlpha(shellLight, 0.96)} />
          <stop offset="50%" stopColor={colorWithAlpha(resolvedPipeColor, 0.94)} />
          <stop offset="100%" stopColor={colorWithAlpha(shellDark, 0.98)} />
        </linearGradient>

        <linearGradient id={cavityGradientId} x1="44" y1="42" x2="276" y2="76" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={colorWithAlpha(cavityLight, 0.9)} />
          <stop offset="100%" stopColor={colorWithAlpha(cavityDark, 0.96)} />
        </linearGradient>

        <linearGradient id={waterGradientId} x1="52" y1="44" x2="52" y2="84" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={colorWithAlpha(waterLight, safeWaterOpacity)} />
          <stop offset="56%" stopColor={colorWithAlpha(waterMid, safeWaterOpacity)} />
          <stop offset="100%" stopColor={colorWithAlpha(waterDark, safeWaterOpacity)} />
        </linearGradient>

        <linearGradient id={clampGradientId} x1="24" y1="28" x2="24" y2="92" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={clampLight} />
          <stop offset="100%" stopColor={clampDark} />
        </linearGradient>

        <linearGradient id={glossGradientId} x1="42" y1="36" x2="278" y2="60" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={colorWithAlpha(glassGlow, 0.46)} />
          <stop offset="100%" stopColor={colorWithAlpha(glassGlow, 0.08)} />
        </linearGradient>

        <clipPath id={clipPathId}>
          <rect x={innerX} y={innerY} width={innerWidth} height={innerHeight} rx={innerRadius} />
        </clipPath>
      </defs>

      <rect x="0" y={outerY} width={outerWidth} height={outerHeight} rx={outerRadius} fill={`url(#${shellGradientId})`} />

      <rect
        className="water-pipe-stage__body-sheen"
        x="10"
        y={outerY + 4}
        width={outerWidth * 0.72}
        height={Math.max(10, outerHeight * 0.24)}
        rx={Math.max(5, outerHeight * 0.12)}
        fill={`url(#${glossGradientId})`}
        opacity="0.62"
      />

      <rect x={innerX} y={innerY} width={innerWidth} height={innerHeight} rx={innerRadius} fill={`url(#${cavityGradientId})`} />

      <g clipPath={`url(#${clipPathId})`}>
        <rect
          x={innerX}
          y={innerY}
          width={innerWidth}
          height={innerHeight * 0.42}
          rx={innerRadius}
          fill={colorWithAlpha(resolvedPipeGlassColor, 0.2)}
        />

        {isFlowing ? (
          <React.Fragment>
            <path className="water-pipe-stage__wave water-pipe-stage__wave--primary" d={primaryWavePath} fill={`url(#${waterGradientId})`} />
            <path className="water-pipe-stage__wave water-pipe-stage__wave--secondary" d={secondaryWavePath} fill={colorWithAlpha(waterLight, safeWaterOpacity * 0.32)} />
            <path className="water-pipe-stage__surface water-pipe-stage__surface--shadow" d={highlightShadowPath} fill={colorWithAlpha(waterDark, 0.18)} />
            <path className="water-pipe-stage__surface water-pipe-stage__surface--highlight" d={highlightBandPath} fill={pipeSurfaceHighlight} />
          </React.Fragment>
        ) : null}
      </g>

      <rect
        x={innerX}
        y={innerY}
        width={innerWidth}
        height={innerHeight}
        rx={innerRadius}
        fill="none"
        stroke={colorWithAlpha(resolvedPipeBorderColor, 0.56)}
        strokeWidth="1.8"
      />

      {clampCenters.map((centerX) => {
        const innerHighlightX = centerX - clampInnerWidth / 2;
        const innerHighlightY = clampBodyY + (clampBodyHeight - clampInnerHeight) / 2;

        return (
          <g key={`${svgIdBase}-clamp-${centerX}`}>
            <rect
              x={centerX - clampBodyWidth / 2}
              y={clampBodyY}
              width={clampBodyWidth}
              height={clampBodyHeight}
              rx={clampBodyRadius}
              fill={`url(#${clampGradientId})`}
            />
            <rect
              x={innerHighlightX}
              y={innerHighlightY}
              width={clampInnerWidth}
              height={clampInnerHeight}
              rx={clampBodyRadius * 0.7}
              fill={clampInnerHighlight}
            />
          </g>
        );
      })}

      <rect
        x="0"
        y={outerY}
        width={outerWidth}
        height={outerHeight}
        rx={outerRadius}
        fill="none"
        stroke={colorWithAlpha(resolvedPipeBorderColor, 0.34)}
        strokeWidth="1.5"
      />
    </g>
  );
};

export const PumpAssemblyElbowGraphic = ({
  idBase,
  title,
  value,
  color,
  pipeColor,
  pipeBackgroundColor,
  pipeGlassColor,
  pipeBorderColor,
  clampColor,
  pipeHeight = 70,
  direction = 'bottom-right',
}) => {
  const instanceIdRef = useRef(`pump-elbow-${Math.random().toString(36).slice(2, 10)}`);
  const isFlowing = getIsFlowing(value);

  const defaultWaterColor = useMemo(
    () => mixColors('var(--icon-reset-password)', 'var(--part-color)', 0.28),
    []
  );
  const defaultShellColor = useMemo(
    () => mixColors('var(--header-color)', 'var(--part-color)', 0.24),
    []
  );
  const defaultPipeGlassColor = useMemo(
    () => mixColors('var(--part-color)', 'var(--cell-background)', 0.24),
    []
  );
  const defaultPipeBorderColor = useMemo(
    () => mixColors(defaultShellColor, 'var(--part-color)', 0.28),
    [defaultShellColor]
  );
  const defaultClampColor = useMemo(
    () => mixColors('var(--header-color)', 'var(--part-color)', 0.18),
    []
  );

  const waterColor = useMemo(
    () => normalizeColor(color, defaultWaterColor, LEGACY_ELBOW_WATER_COLORS),
    [color, defaultWaterColor]
  );
  const shellColor = useMemo(() => {
    const normalized = normalizeColor(pipeColor, defaultShellColor, LEGACY_ELBOW_SHELL_COLORS);
    return colorsEqual(normalized, waterColor) ? defaultShellColor : normalized;
  }, [defaultShellColor, pipeColor, waterColor]);
  const defaultPipeBackgroundColor = useMemo(
    () => mixColors('var(--header-background)', shellColor, 0.14),
    [shellColor]
  );
  const resolvedPipeBackgroundColor = useMemo(
    () => normalizeColor(pipeBackgroundColor, defaultPipeBackgroundColor),
    [defaultPipeBackgroundColor, pipeBackgroundColor]
  );
  const resolvedPipeGlassColor = useMemo(
    () => normalizeColor(pipeGlassColor, defaultPipeGlassColor, [...LEGACY_ELBOW_SHELL_COLORS, ...LEGACY_ELBOW_PIPE_GLASS_COLORS]),
    [defaultPipeGlassColor, pipeGlassColor]
  );
  const resolvedPipeBorderColor = useMemo(
    () => normalizeColor(pipeBorderColor, defaultPipeBorderColor, LEGACY_ELBOW_PIPE_BORDER_COLORS),
    [defaultPipeBorderColor, pipeBorderColor]
  );
  const resolvedClampColor = useMemo(
    () => normalizeColor(clampColor, defaultClampColor, LEGACY_ELBOW_CLAMP_COLORS),
    [clampColor, defaultClampColor]
  );

  const interiorFluidColor = resolvedPipeBackgroundColor;
  const shellLight = useMemo(() => mixColors(shellColor, 'var(--part-color)', 0.35), [shellColor]);
  const shellDark = useMemo(() => mixColors(shellColor, 'var(--shadow-black-soft)', 0.28), [shellColor]);
  const cavityLight = useMemo(
    () => mixColors('var(--header-background)', interiorFluidColor, isFlowing ? 0.22 : 0.08),
    [interiorFluidColor, isFlowing]
  );
  const cavityDark = useMemo(
    () => mixColors('var(--shadow-black-soft)', interiorFluidColor, isFlowing ? 0.08 : 0.18),
    [interiorFluidColor, isFlowing]
  );
  const waterLight = useMemo(() => mixColors(waterColor, 'var(--part-color)', 0.52), [waterColor]);
  const waterMid = useMemo(() => mixColors(waterColor, 'var(--header-background)', 0.24), [waterColor]);
  const waterDark = useMemo(() => mixColors(waterColor, 'var(--shadow-black-soft)', 0.3), [waterColor]);
  const topCapColor = useMemo(
    () => mixColors(resolvedClampColor, shellColor, 0.42),
    [resolvedClampColor, shellColor]
  );
  const topCapInner = useMemo(() => mixColors(topCapColor, 'var(--part-color)', 0.28), [topCapColor]);
  const elbowClampColor = useMemo(
    () => mixColors(resolvedClampColor, topCapColor, 0.24),
    [resolvedClampColor, topCapColor]
  );
  const clampShadow = useMemo(
    () => mixColors(elbowClampColor, 'var(--shadow-black-soft)', 0.24),
    [elbowClampColor]
  );
  const currentHighlightColor = useMemo(
    () => colorWithAlpha(mixColors(waterLight, 'var(--part-color)', 0.18), 0.32),
    [waterLight]
  );
  const glassHighlightColor = useMemo(
    () => colorWithAlpha(mixColors('var(--part-color)', resolvedPipeGlassColor, 0.44), 0.34),
    [resolvedPipeGlassColor]
  );
  const glassFadeColor = useMemo(
    () => colorWithAlpha(mixColors('var(--part-color)', resolvedPipeGlassColor, 0.3), 0),
    [resolvedPipeGlassColor]
  );
  const elbowShadowColor = useMemo(
    () => colorWithAlpha('var(--shadow-black-soft)', 0.24),
    []
  );
  const verticalGlowColor = useMemo(
    () => colorWithAlpha(mixColors('var(--part-color)', waterLight, 0.22), 0.2),
    [waterLight]
  );
  const innerPipeStrokeColor = useMemo(
    () => colorWithAlpha(mixColors('var(--part-color)', resolvedPipeBorderColor, 0.28), 0.42),
    [resolvedPipeBorderColor]
  );
  const shellOutlineColor = useMemo(
    () => colorWithAlpha(mixColors(resolvedPipeBorderColor, 'var(--part-color)', 0.26), 0.28),
    [resolvedPipeBorderColor]
  );
  const clampHighlightColor = useMemo(
    () => colorWithAlpha(mixColors(elbowClampColor, 'var(--part-color)', 0.36), 0.52),
    [elbowClampColor]
  );
  const safePipeHeight = useMemo(
    () => clampValue(Number(pipeHeight) || 70, 20, 108),
    [pipeHeight]
  );
  const elbowScale = useMemo(
    () => safePipeHeight / 70,
    [safePipeHeight]
  );
  const shellStrokeWidth = useMemo(
    () => clampValue(safePipeHeight * 0.66, 16, 62),
    [safePipeHeight]
  );
  const shadowStrokeWidth = useMemo(
    () => clampValue((safePipeHeight * 0.66) + 4, 20, 66),
    [safePipeHeight]
  );
  const cavityMaskStrokeWidth = useMemo(
    () => clampValue(safePipeHeight * (30 / 70), 9, 42),
    [safePipeHeight]
  );
  const verticalCurrentStrokeWidth = useMemo(
    () => clampValue(safePipeHeight * (8 / 70), 2.5, 12),
    [safePipeHeight]
  );
  const verticalGlowStrokeWidth = useMemo(
    () => clampValue(safePipeHeight * (3 / 70), 1.2, 5.5),
    [safePipeHeight]
  );
  const innerCurrentStrokeWidth = useMemo(
    () => clampValue(safePipeHeight * (9 / 70), 3, 13),
    [safePipeHeight]
  );
  const shellOutlineWidth = useMemo(
    () => clampValue(safePipeHeight * (5 / 70), 1.5, 8),
    [safePipeHeight]
  );
  const topCapWidth = useMemo(
    () => clampValue(52 * elbowScale, 42, 74),
    [elbowScale]
  );
  const topCapHeight = useMemo(
    () => clampValue(15 * elbowScale, 11, 22),
    [elbowScale]
  );
  const topCapX = useMemo(
    () => 72 - (topCapWidth / 2),
    [topCapWidth]
  );
  const topCapY = useMemo(
    () => 21.5 - (topCapHeight / 2),
    [topCapHeight]
  );
  const topCapInnerWidth = useMemo(
    () => clampValue(40 * elbowScale, 30, 58),
    [elbowScale]
  );
  const topCapInnerHeight = useMemo(
    () => clampValue(10 * elbowScale, 7, 15),
    [elbowScale]
  );
  const topCapInnerX = useMemo(
    () => 73 - (topCapInnerWidth / 2),
    [topCapInnerWidth]
  );
  const topCapInnerY = useMemo(
    () => 21 - (topCapInnerHeight / 2),
    [topCapInnerHeight]
  );
  const clampRectWidth = useMemo(
    () => clampValue(19 * elbowScale, 14, 28),
    [elbowScale]
  );
  const clampRectHeight = useMemo(
    () => clampValue(46 * elbowScale, 34, 66),
    [elbowScale]
  );
  const clampRectX = useMemo(
    () => 116.5 - (clampRectWidth / 2),
    [clampRectWidth]
  );
  const clampRectY = useMemo(
    () => 97 - (clampRectHeight / 2),
    [clampRectHeight]
  );
  const clampHighlightWidthPx = useMemo(
    () => clampValue(7 * elbowScale, 5, 11),
    [elbowScale]
  );
  const clampHighlightHeightPx = useMemo(
    () => clampValue(31 * elbowScale, 22, 44),
    [elbowScale]
  );
  const clampHighlightX = useMemo(
    () => 116.5 - (clampHighlightWidthPx / 2),
    [clampHighlightWidthPx]
  );
  const clampHighlightY = useMemo(
    () => 95.5 - (clampHighlightHeightPx / 2),
    [clampHighlightHeightPx]
  );
  const glassStripWidth = useMemo(
    () => clampValue(12 * elbowScale, 8, 18),
    [elbowScale]
  );
  const glassStripX = useMemo(
    () => 64 - (glassStripWidth / 2),
    [glassStripWidth]
  );
  const centerStripWidth = useMemo(
    () => clampValue(12 * elbowScale, 8, 18),
    [elbowScale]
  );
  const centerStripX = useMemo(
    () => 80 - (centerStripWidth / 2),
    [centerStripWidth]
  );
  const edgeStripWidth = useMemo(
    () => clampValue(8 * elbowScale, 5, 12),
    [elbowScale]
  );
  const edgeStripX = useMemo(
    () => 54 - (edgeStripWidth / 2),
    [edgeStripWidth]
  );

  const svgIdBase = `${String(idBase || title || 'pump-elbow').replace(/[^A-Za-z0-9_-]/g, '_')}-${instanceIdRef.current}`;
  const shellGradientId = `${svgIdBase}-shell`;
  const cavityGradientId = `${svgIdBase}-cavity`;
  const topWaterGradientId = `${svgIdBase}-top-water`;
  const poolWaterGradientId = `${svgIdBase}-pool-water`;
  const currentWaterGradientId = `${svgIdBase}-current-water`;
  const currentHighlightGradientId = `${svgIdBase}-current-highlight`;
  const verticalCurrentGradientId = `${svgIdBase}-vertical-current`;
  const glassGradientId = `${svgIdBase}-glass`;
  const clampGradientId = `${svgIdBase}-clamp`;
  const cavityMaskId = `${svgIdBase}-mask`;
  const orientationTransform = ELBOW_ORIENTATION_TRANSFORMS[direction] || ELBOW_ORIENTATION_TRANSFORMS['bottom-right'];

  return (
    <g
      style={{
        '--elbow-top-wave-duration': isFlowing ? '2.5s' : '4.1s',
        '--elbow-pool-wave-duration': isFlowing ? '1.6s' : '3.35s',
        '--elbow-pool-wave-secondary-duration': isFlowing ? '2.35s' : '4.8s',
        '--elbow-glass-duration': isFlowing ? '2.7s' : '5s',
      }}
    >
      <defs>
        <linearGradient id={shellGradientId} x1="54" y1="18" x2="138" y2="112" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={colorWithAlpha(shellLight, 0.84)} />
          <stop offset="38%" stopColor={colorWithAlpha(shellColor, 0.78)} />
          <stop offset="100%" stopColor={colorWithAlpha(shellDark, 0.92)} />
        </linearGradient>

        <linearGradient id={cavityGradientId} x1="58" y1="22" x2="136" y2="112" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={colorWithAlpha(cavityLight, 0.82)} />
          <stop offset="100%" stopColor={colorWithAlpha(cavityDark, 0.98)} />
        </linearGradient>

        <linearGradient id={topWaterGradientId} x1="58" y1="24" x2="86" y2="70" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={colorWithAlpha(waterLight, 0.94)} />
          <stop offset="60%" stopColor={colorWithAlpha(waterMid, 0.92)} />
          <stop offset="100%" stopColor={colorWithAlpha(waterDark, 0.84)} />
        </linearGradient>

        <linearGradient id={poolWaterGradientId} x1="42" y1="84" x2="154" y2="116" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={colorWithAlpha(waterMid, 0.92)} />
          <stop offset="60%" stopColor={colorWithAlpha(waterLight, 0.9)} />
          <stop offset="100%" stopColor={colorWithAlpha(waterDark, 0.84)} />
        </linearGradient>

        <linearGradient id={currentWaterGradientId} x1="44" y1="20" x2="146" y2="116" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={colorWithAlpha(waterLight, 0.96)} />
          <stop offset="55%" stopColor={colorWithAlpha(waterMid, 0.94)} />
          <stop offset="100%" stopColor={colorWithAlpha(waterDark, 0.88)} />
        </linearGradient>

        <linearGradient id={currentHighlightGradientId} x1="46" y1="26" x2="92" y2="112" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={currentHighlightColor} />
          <stop offset="100%" stopColor={colorWithAlpha(waterLight, 0.06)} />
        </linearGradient>

        <linearGradient id={verticalCurrentGradientId} x1="78" y1="34" x2="92" y2="98" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={colorWithAlpha(waterLight, 0.94)} />
          <stop offset="60%" stopColor={colorWithAlpha(waterMid, 0.9)} />
          <stop offset="100%" stopColor={colorWithAlpha(waterDark, 0.82)} />
        </linearGradient>

        <linearGradient id={glassGradientId} x1="62" y1="18" x2="94" y2="118" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={glassHighlightColor} />
          <stop offset="100%" stopColor={glassFadeColor} />
        </linearGradient>

        <linearGradient id={clampGradientId} x1="102" y1="74" x2="118" y2="114" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={elbowClampColor} />
          <stop offset="100%" stopColor={clampShadow} />
        </linearGradient>

        <mask id={cavityMaskId}>
          <rect x="0" y="0" width={ELBOW_VIEWBOX.width} height={ELBOW_VIEWBOX.height} fill="black" />
          <path
            d={ELBOW_PIPE_PATH}
            fill="none"
            stroke="white"
            strokeWidth={cavityMaskStrokeWidth}
            strokeLinecap="butt"
            strokeLinejoin="round"
          />
        </mask>
      </defs>

      <g transform={orientationTransform}>
        <path
          d={ELBOW_PIPE_PATH}
          fill="none"
          stroke={elbowShadowColor}
          strokeWidth={shadowStrokeWidth}
          strokeLinecap="butt"
          strokeLinejoin="round"
          transform="translate(2 3)"
        />

        <path
          d={ELBOW_PIPE_PATH}
          fill="none"
          stroke={`url(#${shellGradientId})`}
          strokeWidth={shellStrokeWidth}
          strokeLinecap="butt"
          strokeLinejoin="round"
        />

        <g mask={`url(#${cavityMaskId})`}>
          <rect x="40" y="18" width="120" height="102" fill={`url(#${cavityGradientId})`} />

          {isFlowing ? (
            <g>
              <path
                className="elbow-water-top-surface"
                d="M44 22 H88 V70 C 81 64, 73 60, 64 63 C 56 66, 49 72, 44 68 Z"
                fill={`url(#${topWaterGradientId})`}
              />
              <path
                className="elbow-water-top-sheen"
                d="M50 24 H82 V63 C 76 58, 68 56, 61 59 C 55 61, 52 65, 50 62 Z"
                fill={colorWithAlpha(waterLight, 0.26)}
              />
              <path
                className="elbow-water-pool elbow-water-pool--primary"
                d="M40 90 C 54 82, 68 90, 82 94 C 96 98, 110 88, 124 90 C 140 92, 152 88, 166 91 L166 126 L40 126 Z"
                fill={`url(#${poolWaterGradientId})`}
              />
              <path
                className="elbow-water-pool elbow-water-pool--secondary"
                d="M32 97 C 46 90, 60 98, 74 101 C 88 104, 102 94, 116 97 C 132 100, 146 95, 168 97 L168 126 L32 126 Z"
                fill={colorWithAlpha(waterLight, 0.38)}
              />
              <path
                className="elbow-stage__vertical-current"
                d={ELBOW_VERTICAL_DROP_PATH}
                fill="none"
                stroke={`url(#${verticalCurrentGradientId})`}
                strokeWidth={verticalCurrentStrokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.88"
              />
              <path
                className="elbow-stage__vertical-current-glow"
                d={ELBOW_VERTICAL_DROP_PATH}
                fill="none"
                stroke={verticalGlowColor}
                strokeWidth={verticalGlowStrokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.56"
              />
              <path
                className="elbow-water-current elbow-water-current--primary"
                d="M40 82 C 52 78, 64 86, 78 92 C 92 98, 106 100, 122 97 C 138 94, 152 88, 168 89 L168 109 C 154 111, 140 115, 124 115 C 102 115, 84 109, 68 102 C 56 96, 46 90, 40 90 Z"
                fill={`url(#${currentWaterGradientId})`}
                opacity="0.76"
              />
              <path
                className="elbow-water-current elbow-water-current--secondary"
                d="M40 88 C 54 84, 68 92, 80 97 C 94 103, 108 105, 124 102 C 140 99, 154 94, 168 95 L168 104 C 154 106, 140 110, 124 110 C 102 110, 84 104, 70 98 C 58 93, 48 89, 40 90 Z"
                fill={`url(#${currentHighlightGradientId})`}
                opacity="0.86"
              />
              <path
                d={ELBOW_VERTICAL_DROP_PATH}
                fill="none"
                stroke={innerPipeStrokeColor}
                strokeWidth={innerCurrentStrokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          ) : null}

          <rect
            className="elbow-stage__glass-sheen"
            x={glassStripX}
            y="20"
            width={glassStripWidth}
            height="98"
            rx={glassStripWidth / 2}
            fill={`url(#${glassGradientId})`}
            opacity="0.56"
          />
          <rect x={centerStripX} y="18" width={centerStripWidth} height="102" fill={colorWithAlpha('var(--part-color)', 0.08)} />
          <rect x={edgeStripX} y="18" width={edgeStripWidth} height="102" fill={colorWithAlpha(mixColors(shellColor, 'var(--part-color)', 0.14), 0.08)} />
        </g>

        <path
          d={ELBOW_PIPE_PATH}
          fill="none"
          stroke={shellOutlineColor}
          strokeWidth={shellOutlineWidth}
          strokeLinecap="butt"
          strokeLinejoin="round"
        />

        <rect x={topCapX} y={topCapY} width={topCapWidth} height={topCapHeight} rx={topCapHeight / 2} fill={topCapColor} />
        <rect x={topCapInnerX} y={topCapInnerY} width={topCapInnerWidth} height={topCapInnerHeight} rx={topCapInnerHeight / 2} fill={topCapInner} opacity="0.86" />

        <rect x={clampRectX} y={clampRectY} width={clampRectWidth} height={clampRectHeight} rx={clampRectWidth / 2} fill={`url(#${clampGradientId})`} />
        <rect x={clampHighlightX} y={clampHighlightY} width={clampHighlightWidthPx} height={clampHighlightHeightPx} rx={clampHighlightWidthPx / 2} fill={clampHighlightColor} />
      </g>
    </g>
  );
};
